import { useState, useRef, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, Trash2 } from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import { BoyfriendProfile } from "@/api/models";
import { useTranslation } from "@/hooks/useTranslation";

// Use BoyfriendProfile from API models
type Contact = BoyfriendProfile;

interface SwipeableChatProps {
  contact: Contact;
  isSelected: boolean;
  onSelect: () => void;
  onSwipe: (id: string) => void;
  onDelete: (id: string) => void;
  localFavorites: Set<string>;
  userStyle: 'anime' | 'realistic';
}

export const SwipeableChat = ({
  contact,
  isSelected,
  onSelect,
  onSwipe,
  onDelete,
  localFavorites,
  userStyle
}: SwipeableChatProps) => {
  const { language } = useTranslation();
  const [swipeOffset, setSwipeOffset] = useState(0);

  const formatLastMessageTime = (iso?: string): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 60_000) {
      return language === 'cs' ? 'teď' : 'now';
    }
    const locale = language === 'cs' ? cs : enUS;
    return formatDistanceToNowStrict(date, { addSuffix: true, locale });
  };
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchRef = useRef<HTMLDivElement>(null);

  // Avatar URLs used directly from server

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const currentX = e.touches[0].clientX;
    const diff = startX - currentX;
    
    if (diff > 0 && diff <= 100) {
      setSwipeOffset(diff);
    }
  };

  const handleTouchEnd = () => {
    if (swipeOffset > 50) {
      setSwipeOffset(100);
      onSwipe(contact.id);
    } else {
      setSwipeOffset(0);
    }
    setIsDragging(false);
  };

  useEffect(() => {
    if (!isSelected && swipeOffset > 0) {
      setSwipeOffset(0);
    }
  }, [isSelected, swipeOffset]);

  return (
    <div className="relative overflow-hidden bg-card/60 backdrop-blur-sm">
      <div
        ref={touchRef}
        className="flex items-center transition-transform duration-200 ease-out"
        style={{ transform: `translateX(-${swipeOffset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={onSelect}
      >
        <div className="flex-1 p-4 border-b border-primary/10 hover:bg-card/50 cursor-pointer transition-colors">
          <div className="flex items-start gap-3">
            <div className="relative">
              <Avatar className="h-12 w-12 border-2 border-primary/20">
                <AvatarImage 
                  src={contact.avatarUrl} 
                  alt={contact.name} 
                />
                <AvatarFallback>{contact.name[0]}</AvatarFallback>
              </Avatar>
              {/* Always show online indicator */}
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-background"></div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1">
                  <h3 className="font-semibold truncate">{contact.name}</h3>
                  {localFavorites.has(contact.id) && (
                    <Heart className="h-4 w-4 text-red-500 fill-red-500" />
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{formatLastMessageTime((contact as any).lastMessageTime)}</span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground truncate flex-1 mr-2">
                  {(() => {
                    const text = (contact as any).lastMessageText || contact.bio || "Hey there!";
                    return text.length > 20 ? text.substring(0, 20) + "..." : text;
                  })()}
                </p>
                {(contact as any).unreadCount > 0 && (
                  <Badge variant="destructive" className="text-xs min-w-[20px] h-5 flex items-center justify-center">
                    1
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Delete Button - Only visible when swiping */}
      {swipeOffset > 0 && (
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-destructive flex items-center justify-center">
          <Button
            variant="destructive"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(contact.id);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};